/* =========================================================
   Updates page (v2) — LinkedIn-style feed behaviour.
   - Sidebar search + tag filters (client-side, current page)
   - "See more" expand for capped post content
   - Client-side like (localStorage) with seeded count
   - Comment button toggles native Shopify comments inline
   - Lazy video (IntersectionObserver; loads on expand too)
   - Timeline event links expand the matching post inline
   Loaded on blog + article templates via layout/theme.liquid.
   ========================================================= */
(function () {
  'use strict';

  var HEADER_OFFSET = 80;
  function each(list, fn) { Array.prototype.forEach.call(list, fn); }
  function closest(el, sel) { return el && el.closest ? el.closest(sel) : null; }

  /* ---------- Lazy video ---------- */
  var io = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        each(entries, function (en) {
          if (en.isIntersecting) {
            var el = en.target;
            if (!el.getAttribute('src')) el.setAttribute('src', el.getAttribute('data-src'));
            io.unobserve(el);
          }
        });
      }, { rootMargin: '300px' })
    : null;

  function observeVideos(root) {
    each((root || document).querySelectorAll('[data-src]'), function (el) {
      if (io) io.observe(el);
      else el.setAttribute('src', el.getAttribute('data-src'));
    });
  }
  function loadVideos(root) {
    each(root.querySelectorAll('[data-src]'), function (el) {
      if (!el.getAttribute('src')) el.setAttribute('src', el.getAttribute('data-src'));
    });
  }

  /* ---------- See more ---------- */
  function checkOverflow(content) {
    if (content.classList.contains('is-open')) return; // keep flag while open
    var inner = content.querySelector('.li-content-inner');
    if (!inner) return;
    if (inner.scrollHeight - inner.clientHeight > 4) content.classList.add('is-overflow');
    else content.classList.remove('is-overflow');
  }

  function openContent(content) {
    if (!content || content.classList.contains('is-open')) return;
    content.classList.add('is-open');
    loadVideos(content);
    var btn = content.querySelector('.li-more');
    if (btn) btn.setAttribute('aria-expanded', 'true');
  }

  /* ---------- Likes (client-side) ---------- */
  function likeKey(id) { return 'mp-like-' + id; }
  function isLiked(id) { try { return localStorage.getItem(likeKey(id)) === '1'; } catch (e) { return false; } }
  function setLiked(id, v) { try { v ? localStorage.setItem(likeKey(id), '1') : localStorage.removeItem(likeKey(id)); } catch (e) {} }

  /* ---------- Deep-link / timeline ---------- */
  function openCard(card) {
    openContent(card.querySelector('.li-content'));
    var top = card.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function init() {
    var cards = document.querySelectorAll('.li-card');
    var searchInput = document.querySelector('[data-feed-search]');
    var chips = document.querySelectorAll('.up2-chip');
    var emptyEl = document.querySelector('[data-feed-empty]');
    var activeTag = '';

    /* Filtering (search + active tag) */
    function applyFilters() {
      var q = (searchInput && searchInput.value || '').trim().toLowerCase();
      var anyVisible = false;
      each(cards, function (card) {
        var hay = card.getAttribute('data-search') || '';
        var tags = (card.getAttribute('data-tags') || '').split(',');
        var matchQ = !q || hay.indexOf(q) !== -1;
        var matchTag = !activeTag || tags.indexOf(activeTag) !== -1;
        var show = matchQ && matchTag;
        card.hidden = !show;
        if (show) anyVisible = true;
      });
      if (emptyEl) emptyEl.hidden = anyVisible || cards.length === 0;
    }

    if (searchInput) searchInput.addEventListener('input', applyFilters);
    each(chips, function (chip) {
      chip.addEventListener('click', function () {
        each(chips, function (c) { c.classList.remove('is-active'); });
        chip.classList.add('is-active');
        activeTag = chip.getAttribute('data-filter-tag') || '';
        applyFilters();
      });
    });

    /* Per-card wiring */
    each(cards, function (card) {
      var id = card.getAttribute('data-article-id');
      var content = card.querySelector('.li-content');
      if (content) {
        checkOverflow(content);
        var moreBtn = content.querySelector('.li-more');
        // Beta feeds (main-blog / main-blog-articles) own "See more" via
        // blog-feed.js. If this card lives inside a [data-blog-feed] container,
        // DON'T also wire it here — the two handlers would both toggle .is-open
        // on the same click, cancel out, and the button would do nothing.
        // Legacy feeds (no [data-blog-feed], no blog-feed.js) still use this.
        if (moreBtn && !card.closest('[data-blog-feed]')) {
          moreBtn.addEventListener('click', function () {
            var open = content.classList.toggle('is-open');
            moreBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
            if (open) loadVideos(content);
          });
        }
      }

      var likeBtn = card.querySelector('.li-like');
      if (likeBtn) {
        var seed = parseInt(likeBtn.getAttribute('data-seed') || '0', 10) || 0;
        var countEl = likeBtn.querySelector('.li-like-count');
        var liked = isLiked(id);
        var render = function () {
          likeBtn.classList.toggle('is-liked', liked);
          likeBtn.setAttribute('aria-pressed', liked ? 'true' : 'false');
          if (countEl) countEl.textContent = seed + (liked ? 1 : 0);
        };
        render();
        likeBtn.addEventListener('click', function () {
          liked = !liked; setLiked(id, liked); render();
          if (liked) {
            likeBtn.classList.add('is-pop');
            setTimeout(function () { likeBtn.classList.remove('is-pop'); }, 400);
          }
        });
      }

      var commentBtn = card.querySelector('[data-comment-toggle]');
      var commentBox = card.querySelector('[data-comments]');
      if (commentBtn && commentBox) {
        commentBtn.addEventListener('click', function () {
          var willOpen = commentBox.hasAttribute('hidden');
          if (willOpen) {
            commentBox.removeAttribute('hidden');
            var input = commentBox.querySelector('[data-comment-input]');
            if (input) setTimeout(function () { input.focus(); }, 60);
          } else {
            commentBox.setAttribute('hidden', '');
          }
          commentBtn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        });
      }

      // Existing comments stay collapsed behind a "View N comments" toggle
      var listToggle = card.querySelector('[data-comments-list-toggle]');
      var list = card.querySelector('[data-comments-list]');
      if (listToggle && list) {
        var count = parseInt(listToggle.getAttribute('data-count') || '0', 10) || 0;
        var noun = count === 1 ? ' comment' : ' comments';
        listToggle.addEventListener('click', function () {
          var show = list.hasAttribute('hidden');
          if (show) list.removeAttribute('hidden');
          else list.setAttribute('hidden', '');
          listToggle.setAttribute('aria-expanded', show ? 'true' : 'false');
          listToggle.textContent = show ? 'Hide' + noun : 'View ' + count + noun;
        });
      }
    });

    /* Comment forms submit into a hidden iframe so the feed never navigates away. */
    var sink = document.getElementById('li-comment-sink');
    var pendingForm = null;
    each(document.querySelectorAll('.li-comment-form'), function (form) {
      form.addEventListener('submit', function () { pendingForm = form; });
    });
    if (sink) {
      sink.addEventListener('load', function () {
        if (!pendingForm) return;
        var form = pendingForm; pendingForm = null;
        var box = closest(form, '[data-comments]');
        var moderated = box && box.getAttribute('data-moderated') === 'true';
        var ok = form.querySelector('.li-comment-ok');
        if (!ok) {
          ok = document.createElement('p');
          ok.className = 'up2-subscribe-ok li-comment-ok';
          form.insertBefore(ok, form.firstChild);
        }
        ok.textContent = moderated
          ? 'Thanks — your comment was submitted for review.'
          : 'Thanks — your comment was posted.';
        var body = form.querySelector('textarea[name="comment[body]"]');
        if (body) body.value = '';
      });
    }

    observeVideos(document);

    /* Timeline / estimate links → expand inline when on this page */
    document.addEventListener('click', function (e) {
      var link = closest(e.target, '[data-expand-article]');
      if (!link) return;
      var aid = link.getAttribute('data-expand-article');
      if (!aid) return;
      var card = document.getElementById('post-' + aid);
      if (!card) return; // not in feed → allow navigation
      e.preventDefault();
      openCard(card);
    });

    /* Recompute overflow + open-panel sizing on resize */
    var raf;
    window.addEventListener('resize', function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        each(document.querySelectorAll('.li-content'), checkOverflow);
      });
    });

    /* Re-check overflow after late media load */
    window.addEventListener('load', function () {
      each(document.querySelectorAll('.li-content'), checkOverflow);
    });

    /* Deep link: /blogs/updates#post-<id> */
    if (location.hash && /^#post-\d+/.test(location.hash)) {
      var t = document.getElementById(location.hash.slice(1));
      if (t) setTimeout(function () { openCard(t); }, 80);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
